import { useEffect, useMemo, useState } from 'react';
import { Inbox } from 'lucide-react';
import { api } from '../../api/client';
import { naviguer } from '../../App';
import { PoleDot } from './PoleDot';
import { PenseAVous } from './PenseAVous';
import { TypeBadge } from './StatutBadge';
import { FicheFakOverlay } from '../fak/FicheFakOverlay';
import { ProposerRemplacementModal, PropositionEnregistree, messageErreurDemande, type ResultatProposition } from './ProposerRemplacementModal';
import { formatDateLongue, phrasePenseAVous } from './format';
import { absenceDe, etatRecherche, horaireCourt, jourLocalIso, partiesJour } from './horaire';
import type { Demande } from '../../types';

/** La demande du lien (message Slack) : ce que l'on en sait, épinglé en haut de la liste. */
type Epingle =
  | { genre: 'ouverte'; d: Demande }     // en recherche : on peut s'y proposer
  | { genre: 'close'; d: Demande }       // pourvue ou terminée : on le dit
  | { genre: 'concerne'; d: Demande }    // accès complet (la sienne, désignation en cours, rôle élargi) : on renvoie vers la fiche
  | { genre: 'introuvable' }             // plus disponible pour cette personne
  | { genre: 'erreur' };                 // le chargement a échoué (réseau, serveur) : on ne dit pas « introuvable »

function classer(d: Demande): Epingle {
  if (d.acces === 'close') return { genre: 'close', d };
  if (d.acces === 'limite') return { genre: 'ouverte', d };
  return { genre: 'concerne', d };
}

const jourIso = (d: string | Date) => String(d instanceof Date ? d.toISOString() : d).slice(0, 10);
export const aFra = (d: Demande) => d.fak_statut != null && d.fak_statut !== 'manquant' && d.fak_statut !== 'archive';
const nbDemandes = (n: number) => `${n} demande${n > 1 ? 's' : ''}`;

// La séance la plus proche d'abord (c'est aussi la plus urgente), puis par heure.
function comparer(a: Demande, b: Demande): number {
  return jourIso(a.date_atelier).localeCompare(jourIso(b.date_atelier))
    || (a.atelier_heure_debut ?? '').localeCompare(b.atelier_heure_debut ?? '')
    || a.id - b.id;
}

interface Props {
  /** Liste déjà filtrée par le tableau de bord (pôle choisi, recherche texte). */
  demandes: Demande[];
  /** Augmente à chaque rechargement de la liste depuis le serveur (pas à chaque frappe dans la recherche). */
  versionListe: number;
  chargement: boolean;
  maPersonneId: number | null;
  /** Demande du lien Slack (`#tableau?vue=collegues&demande=ID`), épinglée en haut. */
  epingleId: number | null;
  /** Un pôle ou une recherche texte est en cours : change le message « aucune demande ». */
  filtreActif: boolean;
  scope: 'tous' | 'pole' | 'pole_et_historique';
  /** Recharge la liste (sans spinner) et « Vos remplacements » après une action. */
  onChange: () => void;
}

/**
 * Les trois groupes de la liste : « Pour vous » (un message personnel « … pense à vous ») EN TÊTE, puis « Dans vos pôles »,
 * puis les autres. Chaque demande est dans UN seul groupe (« Pour vous » l'emporte) ; l'ordre dans le groupe est celui reçu
 * (déjà la séance la plus proche d'abord).
 */
export function grouperCollegues(visibles: Demande[]): { pourVous: Demande[]; dansMesPoles: Demande[]; autres: Demande[] } {
  return {
    pourVous: visibles.filter((d) => d.pense_a_vous),
    dansMesPoles: visibles.filter((d) => !d.pense_a_vous && d.dans_mes_poles),
    autres: visibles.filter((d) => !d.pense_a_vous && !d.dans_mes_poles),
  };
}

/**
 * Onglet « Demandes des collègues » (vue intervenant·e) : TOUTES les demandes de
 * remplacement en recherche, quel que soit le pôle, celles qui vous sont adressées personnellement d'abord,
 * puis celles des pôles de la personne. On s'y propose directement (avec récapitulatif et signalement des conflits),
 * ou on masque une demande de son affichage. Voir docs/DECISIONS.md §20.
 */
export function DemandesCollegues({ demandes, versionListe, chargement, maPersonneId, epingleId, filtreActif, scope, onChange }: Props) {
  const aujourdhui = useMemo(() => jourLocalIso(), []);
  const [epingle, setEpingle] = useState<Epingle | null>(null);
  const [epingleCachee, setEpingleCachee] = useState(false);
  // Masquer / réafficher : effet immédiat à l'écran, la liste serveur suit (onChange).
  const [surcharges, setSurcharges] = useState<Map<number, boolean>>(new Map());
  const [masqueesOuvertes, setMasqueesOuvertes] = useState(false);
  const [enCours, setEnCours] = useState<Set<number>>(new Set());
  const [aProposer, setAProposer] = useState<Demande | null>(null);
  const [confirmees, setConfirmees] = useState<Map<number, Demande>>(new Map());
  const [ficheFra, setFicheFra] = useState<{ atelierId: number; demandeId: number } | null>(null);
  const [message, setMessage] = useState<{ genre: 'err'; texte: string } | null>(null);

  // Une autre demande épinglée (nouveau lien) repart de zéro.
  useEffect(() => { setEpingleCachee(false); setEpingle(null); }, [epingleId]);
  // La demande épinglée est lue à part : elle doit rester affichée même si le filtre de pôle ou la
  // recherche texte l'écartent de la liste, et c'est elle qui dit « pourvue » quand elle ne l'est plus.
  useEffect(() => {
    if (epingleId == null) return;
    let annule = false;
    api.getDemande(epingleId)
      .then((d) => { if (!annule) setEpingle(classer(d)); })
      .catch((err: unknown) => {
        if (!annule) setEpingle(err instanceof Error && err.message === 'Demande introuvable' ? { genre: 'introuvable' } : { genre: 'erreur' });
      });
    return () => { annule = true; };
    // versionListe (et non `demandes`) : la recherche texte change `demandes` à chaque frappe, sans rien recharger.
  }, [epingleId, versionListe]);
  // La liste serveur vient d'être rechargée : elle reflète déjà les masquages locaux.
  useEffect(() => { setSurcharges(new Map()); }, [versionListe]);

  // La demande épinglée disparaît dès qu'on l'a masquée ou qu'on s'y est proposé·e (la confirmation prend sa place).
  const epingleDemandeId = epingle && 'd' in epingle ? epingle.d.id : null;
  const epingleAffichee = epingle != null && !epingleCachee && !(epingleDemandeId != null && confirmees.has(epingleDemandeId));
  const epingleOuverte = epingleAffichee && epingle?.genre === 'ouverte' ? epingle.d : null;

  const { visibles, masquees } = useMemo(() => {
    const estMasquee = (d: Demande) => surcharges.get(d.id) ?? !!d.skippee_par_moi;
    // Pas de doublon : la demande épinglée n'est pas répétée dans la liste.
    const lignes = demandes
      .filter((d) => !confirmees.has(d.id) && d.id !== epingleOuverte?.id)
      .sort(comparer);
    return { visibles: lignes.filter((d) => !estMasquee(d)), masquees: lignes.filter(estMasquee) };
  }, [demandes, confirmees, epingleOuverte, surcharges]);

  const { pourVous, dansMesPoles, autres } = grouperCollegues(visibles);
  // Les intitulés de groupe n'apparaissent que s'il y a quelque chose à distinguer : « pour vous » ou « dans vos pôles ».
  const avecGroupes = pourVous.length > 0 || dansMesPoles.length > 0;
  const nomsPoles = [...new Set(dansMesPoles.map((d) => d.pole_nom).filter((n): n is string => !!n))]
    .sort((a, b) => a.localeCompare(b, 'fr'));

  function occupe(id: number, oui: boolean) {
    setEnCours((s) => { const n = new Set(s); if (oui) n.add(id); else n.delete(id); return n; });
  }

  async function changerMasquage(d: Demande, masquer: boolean) {
    occupe(d.id, true);
    setMessage(null);
    try {
      if (masquer) await api.ignorerDemande(d.id); else await api.reafficherDemande(d.id);
      setSurcharges((m) => new Map(m).set(d.id, masquer));
      if (masquer && d.id === epingleId) setEpingleCachee(true);
      onChange();
    } catch (err) {
      setMessage({ genre: 'err', texte: messageErreurDemande(err) });
      onChange();   // la demande a peut-être changé : la liste se met à jour
    } finally {
      occupe(d.id, false);
    }
  }

  function finProposition(d: Demande, resultat: ResultatProposition) {
    if (resultat === 'ok') {
      setConfirmees((m) => new Map(m).set(d.id, d));
      setAProposer(null);
    }
    // Dans tous les cas la demande a pu changer d'état : on recharge (sans spinner).
    onChange();
  }

  // `epinglee` : la demande du lien, dont l'intitulé dit déjà « … pense à vous » (la phrase n'est pas répétée dans la ligne).
  function ligne(d: Demande, estMasquee = false, epinglee = false) {
    return (
      <LigneCollegue
        key={d.id} d={d} aujourdhui={aujourdhui} masquee={estMasquee} occupe={enCours.has(d.id)} sansPenseAVous={epinglee}
        peutSePoser={maPersonneId != null}
        onProposer={() => { setMessage(null); setAProposer(d); }}
        onMasquer={() => changerMasquage(d, true)}
        onReafficher={() => changerMasquage(d, false)}
        onFra={() => setFicheFra({ atelierId: d.atelier_id, demandeId: d.id })}
      />
    );
  }

  const rienAAfficher = visibles.length === 0 && masquees.length === 0 && confirmees.size === 0 && !epingleAffichee;

  return (
    <div className="collegues">
      {message && (
        <div className="badge badge-err"
          style={{ display: 'block', padding: '8px 12px', textTransform: 'none', letterSpacing: 'normal', fontSize: 'var(--t-13)' }}>
          {message.texte}
        </div>
      )}

      {[...confirmees.values()].map((d) => (
        <PropositionEnregistree
          key={`confirmee-${d.id}`} demande={d}
          onVoir={() => naviguer('demandes', d.id)}
          onFra={aFra(d) ? () => setFicheFra({ atelierId: d.atelier_id, demandeId: d.id }) : undefined}
        />
      ))}

      {epingleAffichee && epingle && (
        <div className="collegue-epingle">
          <span className="haut">{epingleOuverte?.pense_a_vous ? phrasePenseAVous(epingleOuverte.pense_a_vous.de) : 'La demande dont on vous a parlé'}</span>
          {epingleOuverte && ligne(epingleOuverte, false, true)}
          {epingle.genre === 'close' && (
            <div style={{ display: 'grid', gap: 2 }}>
              <div className="font-medium">
                {epingle.d.issue === 'pourvue' ? 'Un·e remplaçant·e a été trouvé·e, merci !' : 'Cette recherche est terminée.'}
              </div>
              <div className="text-sm text-ink-2">
                {epingle.d.atelier_nom} · {formatDateLongue(epingle.d.date_atelier)}.{' '}
                {epingle.d.issue === 'pourvue'
                  ? "Il n'y a plus rien à faire pour cette séance."
                  : "Il n'y a plus de remplaçant·e à trouver pour cette séance."}
              </div>
            </div>
          )}
          {epingle.genre === 'concerne' && (
            <div style={{ display: 'grid', gap: 4 }}>
              <div className="font-medium">Vous avez accès à la fiche complète de cette demande.</div>
              <div className="text-sm text-ink-2">{epingle.d.atelier_nom} · {formatDateLongue(epingle.d.date_atelier)}</div>
              <div><button type="button" className="rech-toggle-link" onClick={() => naviguer('demandes', epingle.d.id)}>Ouvrir la fiche</button></div>
            </div>
          )}
          {epingle.genre === 'introuvable' && (
            <div className="text-sm text-ink-2">Cette demande n'est plus disponible. Les demandes en cours sont ci-dessous.</div>
          )}
          {epingle.genre === 'erreur' && (
            <div className="text-sm text-ink-2">Cette demande n'a pas pu être chargée pour le moment. Les demandes en cours sont ci-dessous.</div>
          )}
        </div>
      )}

      {chargement ? (
        <div className="flex justify-center p-24"><div className="spinner" /></div>
      ) : rienAAfficher ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Inbox /></div>
          <div className="empty-state-title">Aucune demande</div>
          <div className="empty-state-desc">
            {filtreActif
              ? 'Aucune demande ne correspond à ces filtres.'
              : scope === 'tous'
              ? 'Aucune demande de remplacement en cours chez vos collègues pour le moment.'
              : `Aucune demande ouverte chez vos collègues pour le moment (${scope === 'pole' ? 'même pôle' : 'même pôle ou personnes déjà remplacées'}).`}
          </div>
        </div>
      ) : (
        <>
          {visibles.length === 0 && masquees.length > 0 && (
            <p className="text-sm text-ink-2" style={{ margin: 0 }}>Aucune demande à afficher : toutes sont masquées.</p>
          )}

          {pourVous.length > 0 && (
            <div className="collegues-groupe">Pour vous <small>{nbDemandes(pourVous.length)}</small></div>
          )}
          {pourVous.map((d) => ligne(d))}

          {dansMesPoles.length > 0 && (
            <div className="collegues-groupe">
              Dans vos pôles <small>{nomsPoles.join(', ')} · {nbDemandes(dansMesPoles.length)}</small>
            </div>
          )}
          {dansMesPoles.map((d) => ligne(d))}

          {avecGroupes && autres.length > 0 && (
            <div className="collegues-groupe">{dansMesPoles.length > 0 ? 'Autres pôles' : 'Autres demandes'} <small>{nbDemandes(autres.length)}</small></div>
          )}
          {autres.map((d) => ligne(d))}

          {masquees.length > 0 && (
            <>
              <div>
                <button type="button" className="rech-toggle-link" aria-expanded={masqueesOuvertes} onClick={() => setMasqueesOuvertes((o) => !o)}>
                  {masqueesOuvertes ? 'Cacher' : 'Afficher'} les demandes masquées ({masquees.length})
                </button>
              </div>
              {masqueesOuvertes && masquees.map((d) => ligne(d, true))}
            </>
          )}
        </>
      )}

      {aProposer && maPersonneId != null && (
        <ProposerRemplacementModal
          demande={aProposer} maPersonneId={maPersonneId}
          onClose={() => setAProposer(null)}
          onFini={(r) => finProposition(aProposer, r)}
        />
      )}
      {ficheFra && (
        <FicheFakOverlay atelierId={ficheFra.atelierId} demandeId={ficheFra.demandeId} onClose={() => setFicheFra(null)} />
      )}
    </div>
  );
}

/**
 * Ce qu'on dit d'une demande à un·e collègue : atelier, pôle, état de la recherche, quand, où, qui est
 * absent·e, fiche relais, et ce qui pourrait gêner (signalé, jamais bloquant). Partagé avec le calendrier.
 */
export function InfosDemandeCollegue({ d, onOuvrir, onFra, marquerVotrePole = false, sansPenseAVous = false }: {
  d: Demande;
  onOuvrir: () => void;
  onFra: () => void;
  /** Dans le calendrier (pas de groupes) : « · votre pôle » après le nom du pôle. */
  marquerVotrePole?: boolean;
  /** La demande épinglée dit déjà « … pense à vous » dans son intitulé : pas de répétition. */
  sansPenseAVous?: boolean;
}) {
  const etat = etatRecherche(d.statut);
  const horaire = horaireCourt(d.atelier_heure_debut, d.atelier_heure_fin);
  return (
    <div className="collegue-infos">
      <div className="collegue-l1">
        <button type="button" className="collegue-nom" onClick={onOuvrir} title="Ouvrir la fiche de la demande">
          {d.atelier_nom}
        </button>
        {d.pole_nom && (
          <span className="inline-flex items-center gap-4">
            <PoleDot couleur={d.pole_couleur} />{d.pole_nom}{marquerVotrePole && d.dans_mes_poles ? ' · votre pôle' : ''}
          </span>
        )}
        {etat.libelle && <span className={`etat-chip${etat.classe ? ` ${etat.classe}` : ''}`}>{etat.libelle}</span>}
        {d.statut !== 'urgence_cascade' && <TypeBadge type={d.type} />}
      </div>
      {d.pense_a_vous && !sansPenseAVous && <PenseAVous de={d.pense_a_vous.de} />}
      <div className="collegue-l2">
        {horaire && <span>{horaire}</span>}
        {d.atelier_lieu && <span>{d.atelier_lieu}</span>}
        <span>{absenceDe(d.absent_nom)}</span>
      </div>
      {aFra(d) && (
        <div>
          <button type="button" className="rech-toggle-link" style={{ fontSize: 'var(--t-13)' }} onClick={onFra}>
            Consulter la fiche relais (FRA)
          </button>
          {d.fak_echelle && <span className="text-ink-3 text-sm"> · {d.fak_echelle}</span>}
        </div>
      )}
      {d.conflits?.anime && (
        <div className="collegue-alerte"><span aria-hidden>⚠</span><span>Vous animez « {d.conflits.anime} » à cette heure.</span></div>
      )}
      {d.conflits?.absente && (
        <div className="collegue-alerte"><span aria-hidden>⚠</span><span>Vous êtes absent·e ce jour-là.</span></div>
      )}
    </div>
  );
}

function LigneCollegue({ d, aujourdhui, masquee, occupe, sansPenseAVous, peutSePoser, onProposer, onMasquer, onReafficher, onFra }: {
  d: Demande;
  aujourdhui: string;
  masquee: boolean;
  occupe: boolean;
  sansPenseAVous: boolean;
  peutSePoser: boolean;
  onProposer: () => void;
  onMasquer: () => void;
  onReafficher: () => void;
  onFra: () => void;
}) {
  const { dow, jour, mois } = partiesJour(d.date_atelier);
  const horaire = horaireCourt(d.atelier_heure_debut, d.atelier_heure_fin);
  const passee = jourIso(d.date_atelier) < aujourdhui;
  const peutProposer = !passee && (d.actions_possibles ?? []).includes('saisir_remplacant');
  // Pas de bouton quand l'état de la demande ne le permet pas (urgence, fusion, annulation à l'étude) :
  // on le dit plutôt que de laisser un vide sans explication.
  const priseEnChargeGestion = !passee && !peutProposer && ['urgence_cascade', 'fusion_demandee', 'evaluation_annulation'].includes(d.statut);
  const libelleDemande = `${d.atelier_nom}, ${dow} ${jour} ${mois}${horaire ? `, ${horaire}` : ''}`;

  return (
    <div className={`collegue-ligne${masquee ? ' masquee' : ''}`}>
      <div className="collegue-date">
        <small>{dow}</small><b>{jour}</b><small>{mois}</small>
      </div>
      <InfosDemandeCollegue d={d} onOuvrir={() => naviguer('demandes', d.id)} onFra={onFra} sansPenseAVous={sansPenseAVous} />
      <div className="collegue-actions">
        {peutProposer && (
          <button type="button" className="btn btn-accent" disabled={occupe || !peutSePoser} aria-label={`Je me propose — ${libelleDemande}`} onClick={onProposer}>
            Je me propose
          </button>
        )}
        {priseEnChargeGestion && <span className="text-sm text-ink-3">Pris en charge par l'équipe gestion</span>}
        {passee && <span className="text-sm text-ink-3">Séance passée</span>}
        {masquee ? (
          <button type="button" className="btn btn-ghost" disabled={occupe} aria-label={`Réafficher — ${libelleDemande}`} onClick={onReafficher}>
            Réafficher
          </button>
        ) : (
          <button type="button" className="btn btn-ghost" disabled={occupe} aria-label={`Masquer — ${libelleDemande}`} onClick={onMasquer}>
            Masquer
          </button>
        )}
      </div>
    </div>
  );
}
